<template>
  <div class="custom-questions-form space-y-4 pb-4">
    <section>
      <h5> {{ $t('create_campaign.lead_form.creation.cq.title') }} </h5>
    </section>

    <section>
      <draggable
        :list="questions"
        :disabled="false"
        class="space-y-2 mb-4"
        ghost-class="ghost"
      >
        <div v-for="(question, qindex) in questions" :key="question.id">
          <!-- Start of collapsed question -->
          <div v-if="question.open !== true" @click="toggleQuestionOpening(question.id)" class="question collapsed-q"
            :class="{'border-red-500': hasErrorOverAllAnswers(question.id) || hasQuestionError(question.id)}">

            <span class="flex space-x-4 items-center">
              <vs-icon icon="speakol-reorder" icon-pack="speakol" color="#C2C2C2"></vs-icon>

              <span class="flex items-center space-x-4">
                <vs-icon :icon="question.enum ? 'speakol-multiple-choice' : 'speakol-short-answer'" icon-pack="speakol"></vs-icon>
                <span>{{ getQuestionLocale(question.title, value.form.lang_iso2_code) }}</span>
              </span>
            </span>

            <span class="flex space-x-4">
              <vs-icon icon="speakol-edit" icon-pack="speakol" color="#0092D7"></vs-icon>

              <vs-icon icon="speakol-cancel" icon-pack="speakol" color="#DB2828" @click="removeQuestion(qindex)"></vs-icon>
            </span>
          </div>
          <!-- End of collapsed question -->

          <!-- Start of Expanded question -->
          <div v-else class="w-full question expanded-q">
            <div class="flex items-top">
              <div class="flex-none w-8" >
                <vs-icon icon="speakol-reorder" icon-pack="speakol" color="#C2C2C2"></vs-icon>
              </div>

              <div class="w-full space-y-3">
                <!-- Start of question input section -->
                <div class="space-y-1">
                  <div @click="toggleQuestionOpening(question.id)">{{$t('create_campaign.lead_form.creation.cq.question')}}</div>
                  <div class="w-full">
                    <div class="w-full flex items-center space-x-4">
                      <v-select
                        :selectable="(option) => !isQuestionPreviouslySelected(option)"
                        :value="getQuestionLocale(question.title, value.form.lang_iso2_code)"
                        :options="questionTypes"
                        :reduce="qType => qType.name"
                        :clearable="false"
                        class="flex-grow w-full font-normal"
                        @input="event => question.title = event"
                      >
                        <template v-slot:option="option">
                          <div class="break-all break-words">
                            {{ option.text[value.form.lang_iso2_code] }}
                          </div>
                        </template>
                      </v-select>

                      <div class="w-8 text-center">
                        <vs-icon icon="speakol-cancel" icon-pack="speakol" color="#DB2828" @click="removeQuestion(qindex)"></vs-icon>
                      </div>
                    </div>

                    <span class="text-danger text-sm block mt-2">{{ getQuestionError(question.id) }}</span>
                  </div>
                </div>
                <!-- End of question input section -->

                <!-- Start of answer type input section -->
                <div class="space-y-1">
                  <div>{{$t('create_campaign.lead_form.creation.cq.answer_type_label')}}</div>
                  <div class="w-full flex items-center">
                    <v-select
                      :options="$constants.LEAD_FORM_ANSWER_TYPES"
                      label="text"
                      :reduce="aType => aType.value"
                      v-model="question.render_type"
                      @input="changeQuestionType(question)"
                      :clearable="false"
                      class="flex-grow w-full font-normal border-red-600"
                    >

                    <template v-slot:option="aType">
                      <div class="flex items-center space-x-4">
                        <vs-icon :icon="aType.icon" icon-pack="speakol"></vs-icon>
                        <span>
                          {{ aType.text }}
                        </span>
                      </div>
                    </template>
                    </v-select>

                    <div class="w-12"></div>
                  </div>
                </div>
                <!-- End of answer type input section -->

                <!-- Start of answers options -->
                <div v-if="question.enum" class="items-center">
                  <div>{{$t('create_campaign.lead_form.creation.cq.answers_list_label')}}</div>
                  <div class="w-full">
                    <draggable
                      :list="question.enum"
                      :disabled="false"
                      class="space-y-2"
                      ghost-class="ghost"
                      @change="resetErrorsOfQuestion(question.id)"
                    >
                      <div v-for="(answer, aindex) in question.enum" :key="`${question.id}${aindex}`" class="answers-parent w-full flex items-center space-x-4">
                        <div class="pb-3">
                          <vs-icon :icon="'speakol-reorder'" icon-pack="speakol" color="#c2c2c2"></vs-icon></vs-icon>
                        </div>

                        <div class="space-y-1">
                          <vs-input
                            :danger="hasAnswerError(question.id, aindex)"
                            class="w-full answer-option"
                            :maxLength="ANSWER_MAX_CHAR_COUNT"
                            v-model="question.enum[aindex]"
                            :placeholder="$t('create_campaign.lead_form.creation.cq.answer_placeholder')"
                            @blur="resetErrorsOfQuestion(question.id)"
                          >
                          </vs-input>
                          <span class="flex justify-between">
                            <div class="answer-error">
                              <div v-if="!hasErrorOverAllAnswers(question.id)" class="text-red-500">{{getAnswerError(question.id, aindex)}}</div>
                              <div v-else-if="hasErrorOverAllAnswers(question.id) && aindex + 1 === question.enum.length" class="text-red-500">{{getAllAnswersError(question.id)}}</div>
                            </div>

                            <div class="answer-counter text-speakol-disabled-color text-base font-normal">{{question.enum[aindex].length}}/{{ANSWER_MAX_CHAR_COUNT}}</div>
                          </span>
                        </div>

                        <div class="flex pb-6 space-x-4">
                          <vs-icon
                            v-if="showPlusIcon(question, aindex)"
                            :class="{'cursor-not-allowed': !answer}"
                            class="plus"
                            icon="speakol-plus"
                            icon-pack="speakol"
                            :color="isThereEmptyAnswer(question.enum) ? '#0092d7' : '#c2c2c2'"
                            @click="addAnswer(question.id, question.enum)"
                          >
                          </vs-icon>

                          <vs-icon
                            v-if="showRemoveIcon(question.enum)"

                            icon="speakol-cancel"
                            icon-pack="speakol"
                            color="#DB2828"
                            @click="removeAnswer(question.id, aindex)"
                          >
                          </vs-icon>
                        </div>
                      </div>
                    </draggable>
                  </div>
                </div>
                <!-- End of answers options -->
              </div>
            </div>
          </div>
        </div>
      </draggable>
    </section>

    <section class="flex">
      <vx-tooltip v-if="questions.length >= MAX_CUSTOM_QUESTIONS" :text="`Maximum ${MAX_CUSTOM_QUESTIONS} Questions.`" position="right" delay=".3s">
        <button class="menu-disabled-btn">
          <span>{{$t('create_campaign.lead_form.creation.cq.menu.btn_add')}}</span>
          <vs-icon icon="speakol-arrow-down-2" icon-pack="speakol" color="text-speakol-color"></vs-icon>
        </button>
      </vx-tooltip>

      <span v-else>
        <button slot="trigger" class="menu-enabled-btn" @click="isNewQuestionMenuOpen = !isNewQuestionMenuOpen">
          <span>{{$t('create_campaign.lead_form.creation.cq.menu.btn_add')}}</span>
          <vs-icon icon="speakol-arrow-down-2" icon-pack="speakol" color="text-speakol-color"></vs-icon>
        </button>

        <dropdown-menu :overlay="false" :isOpen="isNewQuestionMenuOpen" class="questions-drp">
          <ul slot="body" class="p-2">
            <li
              v-for="questionType in questionTypes"
              :key="questionType.name"
               class="flex py-2 px-3 cursor-pointer hover:bg-speakol-color hover:text-white"
               :class="{'disable-question cursor-not-allowed text-speakol-disabled-color hover:bg-transparent': isQuestionPreviouslySelected(questionType)}"
              @click="addQuestion(questionType)"
            >
              {{value.form.lang_iso2_code === 'ar' ? questionType.text.ar : questionType.text.en}}
            </li>
          </ul>
        </dropdown-menu>
      </span>
    </section>
  </div>
</template>
<script>

import draggable from 'vuedraggable'
import FormSchema from '@/utils/LeadFormSchema'
import {cloneDeep, omit} from 'lodash'
import DropdownMenu from 'v-dropdown-menu'
import 'v-dropdown-menu/dist/v-dropdown-menu.css' // Base style, required.
import { LeadFormMixin } from '@/mixins/LeadFormMixin.js'
import { objectToFormData } from '@/helpers/objectToFormData.js'

export default {
  mixins: [LeadFormMixin],
  props: {
    value: {
      required: true,
      type: Object
    },
    serverErrors: {
      required: true
    }
  },
  components: {
    draggable,
    DropdownMenu
  },

  data () {
    return {
      MAX_CUSTOM_QUESTIONS: process.env.VUE_APP_LEAD_FORM_MAX_CUSTOM_QUESTIONS || 4,
      MIN_CUSTOM_QUESTIONS: process.env.VUE_APP_LEAD_FORM_MIN_CUSTOM_QUESTIONS || 2,
      MAX_ANSWERS_COUNT: process.env.VUE_APP_LEAD_FORM_MAX_ANSWERS_COUNT || 12,
      ANSWER_MAX_CHAR_COUNT: 50,

      TYPES: {
        SHORT: 'short',
        MULTIPLE: 'multiple'
      },

      questions: [],
      isNewQuestionMenuOpen: false
    }
  },

  async created () {
    this.questions = Object.values(this.value.form.properties.custom).map(el => {
      el.id = this.generateQuestionId() // unique random id
      el.open = false
      el.render_type = el.enum ? this.TYPES.MULTIPLE: this.TYPES.SHORT

      return cloneDeep(el)
    })
  },

  watch: {
    questions: {
      handler (newQuestionsVals) {
        const questionsSection = document.querySelector('.questions-section .open-item .vs-collapse-item--content')

        if (questionsSection) {
          questionsSection.setAttribute('style', 'max-height: auto !important')
        }

        const primitiveQuestionsLength = Object.keys(this.value.form.properties.primitive).length
        const questions = {}

        newQuestionsVals.forEach((question, index) => {
          const qId = question.id
          question.order = primitiveQuestionsLength + index + 1
          question.format = question.enum && question.enum.length < this.MIN_POPUP_QUESTIONS ? 'radio' : 'string'

          question = omit(question, ['id', 'open', 'render_type'])
          questions[qId] = question
        })

        this.$emit('custom-questions-updated', questions)
      },
      deep: true
    }
  },

  computed: {
    alreadySelectedQuestionsNames () {
      return Object.values(this.questions.map(el => el.title))
    }
  },

  methods: {
    isQuestionPreviouslySelected (option) {
      return this.alreadySelectedQuestionsNames.includes(option.name)
    },

    getQuestionError (qid) {
      return this.$vErrors.first([this.errors, this.serverErrors], `step6.form.properties.${qid}.title`)
    },

    hasQuestionError (qid) {
      // over question title || over any of question props
      return this.$vErrors.has([this.errors, this.serverErrors], `step6.form.properties.${qid}.title`) ||
        new RegExp("step6.form.properties." + qid, 'g').test(JSON.stringify(this.errors))
    },

    hasAnswerError (qid, aindex) {
      return this.$vErrors.has([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum.${aindex}`)
    },

    getAnswerError (qid, aindex) {
      return this.$vErrors.first([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum.${aindex}`)
    },

    hasErrorOverAllAnswers (qid) {
      return this.$vErrors.has([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum`)
    },

    getAllAnswersError (qid) {
      return this.$vErrors.first([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum`)
    },

    toggleQuestionOpening (id) {
      this.questions = this.questions.map(question => {
        question.open = question.id === id ? !question.open : false

        return question
      })

      this.resetErrorsOfQuestion(id)
    },

    changeQuestionType (question) {
      if (question.render_type === this.TYPES.SHORT) {
        delete question.enum

        question.options = cloneDeep(FormSchema).properties.short_answer.options
      } else {
        delete question.options

        this.$set(question, 'enum', ['', '']) // make it reactive

        this.handleQuestionsListChange()
      }
    },

    generateQuestionId () {
      return `${ Math.random().toString(16).slice(2)}spkl_id`
    },

    addQuestion (questionType) {
      if (this.isQuestionPreviouslySelected(questionType)) {
        return
      }

      this.isNewQuestionMenuOpen = false

      const q = FormSchema.properties.short_answer

      q.id = this.generateQuestionId()
      q.open = false
      q.title = questionType.name
      q.render_type = this.TYPES.SHORT

      this.questions.push(cloneDeep(q))

      this.toggleQuestionOpening(q.id)

      this.handleQuestionsListChange()
    },

    removeQuestion (index) {
      this.questions.splice(index, 1)

      this.handleQuestionsListChange()
    },

    async addAnswer (questionId, answers) {
      // Prevent adding new answer if prev one is empty
      if (!this.isThereEmptyAnswer(answers)) {
        return
      }

      this.getQuestionAnswers(questionId).push('')

      await this.resetErrorsOfQuestion(questionId)

      // Remove error for the latest added answer
      this.removeErrorsOfAnswer(questionId, this.getQuestionAnswers(questionId).length - 1)

      this.handleQuestionsListChange()
    },

    removeAnswer (questionId, index) {
      this.getQuestionAnswers(questionId).splice(index, 1)

      this.handleQuestionsListChange()

      this.resetErrorsOfQuestion(questionId)
    },

    async validateQuestionOfId(qid) {
      let form = {}

      await this.removeErrorsOfQuestion(qid)

      form['step6'] = {
        form: {
          properties: {
            custom: {
              [qid]: this.value.form.properties.custom[qid]
            }
          }
        }
      }

      try {
        await window.axios.post(`/campaigns/custom-questions/valiadate`, objectToFormData(form), {
          hideLoader: true
        })
      } catch(error) {
        this.$setLaravelValidationErrorsFromResponse(error.response.data)
      }
    },

    async resetErrorsOfQuestion(qid) {
      await Promise.all([this.removeErrorsOfQuestion(qid), this.validateQuestionOfId(qid)])
    },

    getQuestionAnswers (questionId) {
      return this.value.form.properties.custom[questionId].enum
    },

    isThereEmptyAnswer (answers) {
      return !answers.includes('')
    },

    showPlusIcon (question, aindex) {
      const answersLen = question.enum && question.enum.length

      return answersLen < this.MAX_ANSWERS_COUNT &&
        answersLen === aindex + 1
    },

    showRemoveIcon (answers) {
      return answers.length > this.MIN_CUSTOM_QUESTIONS
    },

    handleQuestionsListChange () {
      this.$emit('questions-change')
    },

    removeErrorsOfQuestion(qid) {
      this.$vErrors.remove([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum`)

      this.getQuestionAnswers(qid).forEach((el, aindex) => {
        this.removeErrorsOfAnswer(qid, aindex)
      })
    },

    removeErrorsOfAnswer(qid, aindex) {
      this.$vErrors.remove([this.errors, this.serverErrors], `step6.form.properties.${qid}.enum.${aindex}`)
    }
  }
}
</script>

<style lang="scss" scoped>

.custom-questions-form {
  /deep/ .v-select .vs__dropdown-menu .vs__dropdown-option--highlight {
    padding-top: 5px;
    padding-bottom: 5px;
  }

  /deep/ .vs__dropdown-option--disabled {
    @apply
      cursor-not-allowed
      opacity-50;
  }

  /deep/ .span-text-validation-danger {
    display: none;
  }

  /deep/ .vs-icon:not(.speakol-arrow-down-2) {
    font-size: 17px !important;
  }

  /deep/ .vs-input--placeholder {
    font-size: 14px !important;
  }

  .answer-option {
    width: 336px !important;
  }

  .answer-error {
    font-size: 12px;
  }

  .questions-drp {
    max-height: 200px;
    font-size: 14px;
    width: 262px;
    color: #323232;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
  }

  .menu-btn {
    width: 152px;

    @apply
      flex
      justify-between
      items-center
      h-12
      text-base
      pl-5
      pr-3
      bg-white
      border
      border-solid
      rounded
      cursor-pointer;
  }

  .menu-enabled-btn {
    @extend .menu-btn;

    @apply
      text-speakol-color
      border-speakol-color;
  }

  .menu-disabled-btn {
    @extend .menu-btn;

    @apply
      text-speakol-disabled-color
      border-speakol-disabled-color;
  }

  .question {
    @apply
      p-5
      border-solid
      border
      border-speakol-disabled-color
      rounded;

    &.expanded-q {
      @apply
        w-full
        justify-items-end;
    }

    &.collapsed-q {
      @apply
        flex
        h-16
        flex-grow
        justify-between
        items-center;
    }
  }

  .vs-dropdown--menu--after,
  .vs-dropdown-right--menu--after {
    display: none !important;
  }

  .answers-parent {
    margin-top: 10px !important;
  }  

  .answers-parent:first-child {
    margin-top: 0px !important;
  }  

  @media (max-width: 768px) {
    /deep/ .vs__selected {
      text-overflow: ellipsis;
      overflow: hidden; 
      width: 190px; 
      white-space: nowrap;
      display: block !important;
    }

    /deep/ .vs__dropdown-menu {
      max-width: 200px !important;
    }

    .question {
      @apply p-3;
    }

    .answer-option {
      width: 150px !important;
    }

    .answer-error {
      max-width: 140px;
    }
  }
}
</style>
